"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, FileText, Flame, ShieldCheck, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function NoxHero() {
  const shouldReduceMotion = useReducedMotion();
  const features = [{
    title: "Tecnologia SNCR e SCR",
    description: "Injeção calibrada de amoníaco e ureia com atomização fina para máxima reação química.",
    icon: Flame
  }, {
    title: "Lanças Injetoras AutoJet",
    description: "Controlo dinâmico de caudal e dimensionamento personalizado para gases a alta temperatura.",
    icon: Activity
  }, {
    title: "Conformidade e Eficiência",
    description: "Redução de emissões poluentes cumprindo rigorosamente os limites legais industriais.",
    icon: ShieldCheck
  }];
  return <section id="herocontrolonox" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-20 md:py-28 lg:py-32">
      {/* Decorative subtle background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-40 size-[520px] rounded-full bg-secondary/15 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 size-[420px] rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-secondary/10 via-transparent to-transparent opacity-60" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Main Hero Copy */}
          <div className="lg:col-span-7">
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="space-y-6">
              <div className="inline-flex items-center gap-2">
                <Badge variant="secondary" className="rounded-full px-3.5 py-1 text-xs font-semibold tracking-wide uppercase shadow-xs">
                  <Sparkles className="size-3.5 mr-1" />
                  Despoluição e Controlo de Emissões
                </Badge>
              </div>

              <h1 className="font-default text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl lg:text-6xl text-primary-foreground">
                Controlo de NOx e emissões industriais
              </h1>

              <p className="max-w-2xl text-base sm:text-lg md:text-xl text-primary-foreground/90 font-normal leading-relaxed">
                Sistemas de injeção de reagentes para processos SNCR e SCR com bicos de pulverização de alta precisão Spraying Systems Co., garantindo neutralização eficaz e conformidade ambiental rigorosa.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md font-medium px-6 py-6 text-base">
                  <Link to="/ContactsPage">
                    Consulte-nos
                    <ArrowRight className="size-5" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground px-6 py-6 text-base">
                  <Link to="/TechnicalSolutionsPage">
                    <FileText className="size-5" />
                    Soluções Técnicas
                  </Link>
                </Button>
              </div>

              {/* Bullet proofpoints */}
              <div className="pt-4 border-t border-primary-foreground/15 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-primary-foreground/85">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-secondary shrink-0" />
                  <span>Representante Spraying Systems Co. desde 1993</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-secondary shrink-0" />
                  <span>Modelação CFD e lanças sob especificação</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Cards & Highlight Column */}
          <div className="lg:col-span-5">
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.15,
            ease: "easeOut"
          }} className="space-y-4">
              {features.map((feat, i) => {
              const IconComponent = feat.icon;
              return <Card key={feat.title} data-index={i} className="border border-primary-foreground/15 bg-primary/70 backdrop-blur-md text-primary-foreground shadow-lg transition-[border-color,background-color] hover:border-secondary/50 hover:bg-primary/90">
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-secondary/20 text-secondary shrink-0 border border-secondary/30">
                        <IconComponent className="size-5" />
                      </div>
                      <div className="space-y-1">
                        <h2 className="font-default text-base font-semibold text-primary-foreground leading-snug">
                          {feat.title}
                        </h2>
                        <p className="text-sm text-primary-foreground/80 leading-relaxed">
                          {feat.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>;
            })}

              {/* Catalogs quick link card */}
              <div className="p-4 rounded-xl bg-secondary/15 border border-secondary/30 flex items-center justify-between gap-4">
                <div className="text-xs sm:text-sm text-primary-foreground/90 font-medium">
                  Documentação técnica e catálogos disponíveis para descarregar.
                </div>
                <Button asChild variant="outline" size="sm" className="shrink-0 border-secondary/40 bg-transparent text-primary-foreground hover:bg-secondary hover:text-secondary-foreground">
                  <Link to="/CatalogsPage">Ver Catálogos</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}